/**
 * Public runtime configuration values.
 *
 * Injected into window.__ENV by _document.tsx (server-side on every request),
 * so values are available on the client from first paint with no extra network request.
 *
 * Use `getRuntimeConfig()` anywhere — in React components, utility functions, or event handlers.
 * Use `getRuntimeConfigValue(key)` as a shorthand when only one key is needed.
 *
 * To add a new variable, add a single entry to RUNTIME_CONFIG_SCHEMA below.
 * The IRuntimeConfig type, empty defaults, and SSR reader are all derived automatically.
 *
 * SAFETY: every envVar MUST start with "NEXT_PUBLIC_". This is enforced at compile
 * time by the `TPublicEnvVar` constraint — any entry whose envVar does not start with
 * that prefix will produce a TypeScript error. This prevents accidentally serialising
 * server-only secrets into the HTML response via window.__ENV.
 *
 * @see src/pages/_document.tsx
 * @see src/utils/runtime-config.ts
 */

type TPublicEnvVar = `NEXT_PUBLIC_${string}`;

type TSchemaEntry = { envVar: TPublicEnvVar; type: "string" | "boolean" };

/**
 * Each entry maps a config key to its env-var name and value type.
 * Adding a row here is the only change needed to expose a new env variable.
 *
 * The `envVar` field is constrained to `NEXT_PUBLIC_*` — TypeScript will reject any
 * server-only variable name at the point of definition.
 */
export const RUNTIME_CONFIG_SCHEMA = {
    imgixUrl: { envVar: "NEXT_PUBLIC_IMGIX_URL", type: "string" },
    chartImagePreviewUrl: { envVar: "NEXT_PUBLIC_CHART_IMAGE_PREVIEW_URL", type: "string" },
    cloudBlobEnvironmentShortName: { envVar: "NEXT_PUBLIC_CLOUD_BLOB_ENVIRONMENT_SHORTNAME", type: "string" },
    cloudBlobElysiumAppPathSegment: { envVar: "NEXT_PUBLIC_CLOUD_BLOB_ELYSIUM_APP_PATH_SEGMENT", type: "string" },
    searchAutoCompleteEndpoint: { envVar: "NEXT_PUBLIC_SEARCH_AUTO_COMPLETE_ENDPOINT", type: "string" },
    version: { envVar: "NEXT_PUBLIC_VERSION", type: "string" },
    dynatraceRumEnabled: { envVar: "NEXT_PUBLIC_DYNATRACE_RUM_ENABLED", type: "boolean" },
    dynatraceRumTagUrl: { envVar: "NEXT_PUBLIC_DYNATRACE_RUM_TAG_URL", type: "string" },
} as const satisfies Record<string, TSchemaEntry>;

type InferType<T extends TSchemaEntry> = T["type"] extends "boolean" ? boolean : string;

export type IRuntimeConfig = {
    [K in keyof typeof RUNTIME_CONFIG_SCHEMA]: InferType<(typeof RUNTIME_CONFIG_SCHEMA)[K]>;
};
